<template>
  <v-container fluid class="pa-1">
    <v-navigation-drawer app clipped permanent floating width="250">
      <template #prepend>
        <v-menu offset-y close-on-content-click>
          <template #activator="{ on, attrs }">
            <v-btn color="primary" class="ma-6" v-bind="attrs" v-on="on">+ Add new</v-btn>
          </template>
          <v-card max-width="300" class="mx-auto">
            <v-list dense>
              <!-- Case Creation Dialog -->
              <DialogForm title="Create new case" max-width="600px" :submit="addCase(newCaseName)">
                <template #activator="{ on: onDialog }">
                  <v-list-item v-on="onDialog">
                    <v-list-item-icon>
                      <v-icon>mdi-file-plus-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content> Case </v-list-item-content>
                  </v-list-item>
                </template>
                <template #default>
                  <v-text-field
                    v-model="newCaseName"
                    label="Name"
                    placeholder="New Case"
                    counter="64"
                    :rules="[
                      $utils.validation.required,
                      $utils.validation.counter(64),
                      $utils.validation.specialCharacters,
                    ]"
                    maxlength="64"
                  ></v-text-field>
                </template>
              </DialogForm>
              <!-- <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-chart-box-plus-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content> Forecast </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-delta</v-icon>
                </v-list-item-icon>
                <v-list-item-content> Delta </v-list-item-content>
              </v-list-item> -->
              <v-divider></v-divider>

              <!-- Folder Creation Dialog -->
              <DialogForm
                title="Create new folder"
                max-width="600px"
                :submit="createFolder(newFolderName)"
              >
                <template #activator="{ on: onDialog }">
                  <v-list-item v-on="onDialog">
                    <v-list-item-icon>
                      <v-icon v-on="onTooltip">mdi-folder-plus-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content> Folder </v-list-item-content>
                  </v-list-item>
                </template>
                <template #default>
                  <v-text-field
                    v-model="newFolderName"
                    label="Name"
                    placeholder="New Folder"
                    counter="64"
                    :rules="[
                      $utils.validation.required,
                      $utils.validation.counter(64),
                      $utils.validation.specialCharacters,
                    ]"
                    maxlength="64"
                  ></v-text-field>
                </template>
              </DialogForm>
            </v-list>
          </v-card>
        </v-menu>
      </template>
      <v-list dense nav>
        <v-list-item
          link
          :to="{
            name: 'Home',
          }"
        >
          <v-list-item-icon>
            <v-icon>mdi-home</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Home</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link :to="{ name: 'Folders' }">
          <v-list-item-icon>
            <v-icon>mdi-folder</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Folders</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <FolderTreeSelect @input="toFolder" :opened-root="false"></FolderTreeSelect>
        </v-list-item>
        <v-list-item link :to="{ name: 'RecycleBin' }">
          <v-list-item-icon>
            <v-icon>mdi-delete</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Recycle Bin</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item link :to="{ name: 'Jobs' }">
          <v-list-item-icon>
            <v-icon>mdi-run</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Jobs</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-badge v-if="countRunningJobs" color="primary" :content="countRunningJobs" inline>
            </v-badge>
          </v-list-item-action>
        </v-list-item>
        <v-list-item link :to="{ name: 'Deltas' }">
          <v-list-item-icon>
            <v-icon>mdi-triangle</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Deltas</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link :to="{ name: 'Forecasts' }">
          <v-list-item-icon>
            <v-icon>mdi-chart-box</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Forecasts</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <transition>
      <keep-alive>
        <router-view></router-view>
      </keep-alive>
    </transition>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "Storage",
  components: {
    FolderTreeSelect: () => import("@/components/FolderTreeSelect.vue"),
    DialogForm: () => import("@/components/DialogForm.vue"),
  },
  mixins: [],
  props: {},
  data() {
    return {
      pages: [],
      newCaseName: "",
      newFolderName: "",
    };
  },
  computed: {
    ...mapState("jobs", { areJobsLoading: "isFindPending" }),
    ...mapGetters("jobs", { findJobsInStore: "find" }),
    runningJobsQuery() {
      return { status: { $in: ["NOT_STARTED", "IN_PROGRESS"] } };
    },
    countRunningJobs() {
      return this.findJobsInStore({ query: this.runningJobsQuery }).data.length;
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      createDirectory: "directories/create",
      createCase: "cases/create",
    }),
    toFolder($e) {
      this.$router
        .push({ name: "Folders", params: { folderId: $e.id === "root" ? null : $e.id } })
        .catch(() => {});
    },
    addCase(name) {
      return () => {
        return this.createCase({
          name,
          directory_id: this.newCaseFolder || this.directoryId,
        }).then(() => {
          this.newCaseName = "";
          // This action should refresh folder's storage
        });
      };
    },
    createFolder(folderName) {
      return () => {
        return this.createDirectory({
          name: folderName,
          ...(this.currentFolder ? { path: this.currentFolder.path } : {}),
        }).then(() => {
          this.newFolderName = "";
          // This action should refresh case's storage
        });
      };
    },
  },
};
</script>

<style scoped></style>
